/* eslint-disable no-param-reassign */
import { BehaviorSubject } from 'rxjs';

import ReportThumbnail from '../assets/images/report-thumbnail.png';
import ListAdditionalFilesDialog from '../components/additionalFiles/ListAdditionalFilesDialog';
import AmbraContainer from '../components/ambraContainer/AmbraContainer';
import UndoCaseSignOffModal from '../components/caseDetails/UndoCaseSignOffModal';
import HtmlWrapper from '../components/common/HtmlWrapper';
import ImageWrapper from '../components/common/ImageWrapper';
import VideoWrapper from '../components/common/VideoWrapper';
import ObjViewer from '../components/objViewer/ObjViewer';
import PatientReviewReportSent from '../components/patientReviewReportSent/PatientReviewReportSent';
import PatientShareVideoReportPopup from '../components/patientShareVideoReportPopup/PatientShareVideoReportPopup';
import PdfViewer from '../components/pdfViewer/PdfViewer';
import ReportViewer from '../containers/report-viewer/ReportViewer';
import Case from '../models/Case';
import { DCM, DOCS, IMAGE, THREE, VIDEO } from '../models/Resource';

const defaultReportRate = {
  ratingComment: '',
  ratingScore: 0,
  submitted: false,
};

export default class PatientReportBloc {
  caseDetails$ = new BehaviorSubject(null);
  selectedVideo$ = new BehaviorSubject(null);
  reportRate$ = new BehaviorSubject(defaultReportRate);
  loadingReportRate$ = new BehaviorSubject(false);
  videoPlayed$ = new BehaviorSubject(false);
  shouldReopenAdditionalFilesDialog$ = new BehaviorSubject(false);

  constructor({ appRepository, authRepository, casesRepository, patientRepository, dialogRepository, errorRepository, reportViewerBloc, userRepository, resourceRepository }) {
    this.casesRepository = casesRepository;
    this.dialogRepository = dialogRepository;
    this.patientRepository = patientRepository;
    this.errorRepository = errorRepository;
    this.reportViewerBloc = reportViewerBloc;
    this.userRepository = userRepository;
    this.resourceRepository = resourceRepository;
    this.appRepository = appRepository;
    this.authRepository = authRepository;
  }

  async getRecordByCaseId(caseId) {
    let currentCase = this.selectedCase$.getValue();

    if (!currentCase?.id) {
      const caseStudy = await this.casesRepository.getCase(caseId);
      const frameUrl = await this.casesRepository.getLinkViewer(caseId);
      caseStudy.viewerLink = frameUrl.viewerLink;
      const selectedCase = new Case(caseStudy);
      this.selectedCase$.next(selectedCase);
    }

    currentCase = this.selectedCase$.getValue();
    const recordId = currentCase?.dicomStudy?.id;

    if (recordId) await this.getRecordById(recordId);
  }

  async getRecordById(id) {
    const record = await this.patientRepository.getRecord(id);
    if (record && record.case) {
      const frameUrl = await this.casesRepository.getLinkViewer(record.case.id);
      record.case.viewerLink = frameUrl.viewerLink;
    } else {
      return this.errorRepository.error$.next({
        message: 'Could not get the case for this record',
        severity: 'error',
      });
    }
    const caseObj = new Case(record.case);
    const videos = caseObj.supplementaryMaterials?.length
      ? caseObj.supplementaryMaterials.map((material) => {
          return {
            description: material?.material?.description,
            id: material?.material?.id,
            isPlaying: false,
            thumbnail: material?.material?.resource?.thumbnailPath,
            title: material?.material.title,
            type: 'SUPPLEMENTARY MATERIAL',
            url: material?.material?.resource?.path,
          };
        })
      : [];
    const videoReport = {
      id: null,

      isPlaying: true,
      // todo: check how to get the video report thumbnail
      thumbnail: caseObj.videoReport?.thumbnailPath || ReportThumbnail,
      title: 'Your Personal Video Report',
      type: 'VIDEO REPORT',
      url: caseObj.videoReport?.h264?.downloadLink || caseObj.videoReport?.downloadLink,
    };
    videos.push(videoReport);
    this.selectedVideo$.next(videoReport);
    const note = caseObj.workspace?.globalVideoIntroduction;
    this.caseDetails$.next({
      case: caseObj,
      id: record.id,
      note,
      videos,
    });
    this.reportRate$.next({
      ratingComment: caseObj.ratingComment || '',
      ratingScore: caseObj.ratingScore || 0,
      submitted: caseObj?.ratingScore || false,
    });
    return null;
  }

  selectVideo(video) {
    const { videos } = this.caseDetails$.getValue();
    const selectedVideo = videos.find((item) => item.id === video.id);
    videos.forEach((item) => {
      item.isPlaying = item === selectedVideo;
    });
    this.caseDetails$.next({
      ...this.caseDetails$.getValue(),
      videos,
    });
    this.selectedVideo$.next(selectedVideo);
  }

  onReportRateChange(field, value) {
    const reportRate = this.reportRate$.getValue();
    this.reportRate$.next({
      ...reportRate,
      [field]: value,
    });
  }

  async submitReportRate() {
    const reportRate = this.reportRate$.getValue();
    const caseDetail = this.caseDetails$.getValue();

    try {
      this.loadingReportRate$.next(true);
      if (!caseDetail?.case?.id) throw new Error('No case found');
      await this.casesRepository.submitRate(caseDetail.case.id, reportRate);
      this.onReportRateChange('submitted', true);
      this.handleOpenReviewSent();
    } catch (error) {
      this.errorRepository.error$.next({
        message: error?.message || 'Failed to submit report rate',
        severity: 'error',
      });
    } finally {
      this.loadingReportRate$.next(false);
    }
  }

  handleOpenReviewSent() {
    this.dialogRepository.loadDialog(PatientReviewReportSent, {
      onClose: () => this.dialogRepository.toggleDialog(false),
    });
  }

  handleOpenSharePopup() {
    const resourceId = this.caseDetails$.getValue()?.id;
    this.dialogRepository.loadDialog(PatientShareVideoReportPopup, {
      resourceId,
    });
  }

  handleRevertSignOffPopup() {
    this.dialogRepository.loadDialog(UndoCaseSignOffModal, {
      onCancel: () => this.dialogRepository.toggleDialog(false),
      onCloseIconClick: () => this.dialogRepository.toggleDialog(false),
      onConfirm: () => {
        this.revertCaseSignOff();
        this.dialogRepository.toggleDialog(false);
      },
    });
  }

  async revertCaseSignOff() {
    const caseId = this.caseDetails$.getValue()?.case?.id;

    this.casesRepository.revertSignOff(caseId);
  }

  viewReport(dialogClasses) {
    const caseObj = this.caseDetails$.getValue()?.case;

    this.dialogRepository.loadDialog(ReportViewer, {
      dialogClasses,
      reportViewerBloc: this.reportViewerBloc,
      resources: caseObj?.reports,
    });
  }

  async handlePlayVideo() {
    const caseDetails = this.caseDetails$.getValue();
    const userDetails = this.userDetails$.getValue();
    const isVideoPlayed = this.videoPlayed$.getValue();
    if (!caseDetails?.id || userDetails.userViewedReportFirstTime || isVideoPlayed) return;

    try {
      this.videoPlayed$.next(true);

      if (userDetails?.role === 'patient' || caseDetails?.case?.patientId === userDetails?.id) {
        await this.patientRepository.viewReportFirstTime(caseDetails.id);
      }

      this.userRepository.getCurrentUser();
    } catch (err) {
      this.videoPlayed$.next(false);
    }
  }

  async viewStudy() {
    try {
      this.appRepository.handleOpenLoading();

      const caseObj = this.caseDetails$.getValue()?.case;

      const fileType = caseObj.studyFileType || 'dcm';
      const viewerLink = caseObj?.viewerLink;

      const title = caseObj?.dicomStudy?.description || caseObj?.dicomStudy?.title;

      let childResources = [];
      if (fileType === 'dcm' || ![VIDEO, IMAGE, 'pdf', THREE, DOCS].includes(fileType)) {
        childResources = (await this.patientRepository.getChildResourcesLink(caseObj.dicomStudy?.id)) || [];
      }

      if (fileType === VIDEO) {
        this.dialogRepository.loadDialog(VideoWrapper, { url: viewerLink });
      } else if (fileType === IMAGE) {
        this.dialogRepository.loadDialog(ImageWrapper, { url: viewerLink });
      } else if (fileType === THREE) {
        this.dialogRepository.loadDialog(ObjViewer, { url: viewerLink });
      } else if (fileType === 'pdf' || fileType === DOCS) {
        this.dialogRepository.loadDialog(PdfViewer, { url: viewerLink });
      } else {
        this.dialogRepository.loadDialog(AmbraContainer, { childResources, onClose: () => this.dialogRepository.toggleDialog(false), title, url: viewerLink });
      }
    } finally {
      this.appRepository.handleCloseLoading();
    }
  }

  async viewCaseReport(content) {
    this.dialogRepository.loadDialog(
      HtmlWrapper,
      {
        content,
        onClose: () => {
          this.closeDialog();
        },
        onCrossIconClick: () => {
          this.closeDialog();
        },
      },
      true,
      true
    );
  }

  getResourceByUuid(uuid) {
    return this.resourceRepository.getResourceInfo(uuid);
  }

  closeDialog() {
    this.dialogRepository.toggleDialog(false);
  }

  openViewMoreAdditionalFilesDialog(files) {
    this.dialogRepository.loadDialog(ListAdditionalFilesDialog, {
      files,
      onCrossIconClick() {
        this.shouldReopenAdditionalFilesDialog$.next(false);
        this.closeDialog();
      },
      onViewFile(file) {
        this.shouldReopenAdditionalFilesDialog$.next(true);
        this.closeDialog();
        this.openViewer(file);
      },
      showDownloadButton: true,
    });
  }

  checkReopenAdditionalFilesDialog() {
    const shouldReopen = this.shouldReopenAdditionalFilesDialog$.getValue();
    if (!shouldReopen) return;

    const caseDetails = this.caseDetails$.getValue();

    this.shouldReopenAdditionalFilesDialog$.next(false);
    this.openViewMoreAdditionalFilesDialog(caseDetails?.case?.additionalFiles || []);
  }

  async openViewer(material) {
    if (material.type === DCM) {
      try {
        // Should reload the resource info because the viewerLink may have expired
        const resourceInfo = await this.getResourceByUuid(material.uuid);
        this.dialogRepository.loadDialog(AmbraContainer, {
          onClose: () => {
            this.dialogRepository.toggleDialog(false);
            this.checkReopenAdditionalFilesDialog();
          },
          url: resourceInfo?.viewerLink,
        });
        // eslint-disable-next-line no-empty
      } catch (error) {
        this.errorRepository.error$.next({
          message: error?.message || 'Failed to get resource info!',
          severity: 'error',
        });
      }
    }

    if (material.type === IMAGE) {
      this.dialogRepository.loadDialog(ImageWrapper, {
        onClose: () => {
          this.checkReopenAdditionalFilesDialog();
        },
        url: material.downloadLink,
      });
    }

    if (material.type === THREE) {
      this.dialogRepository.loadDialog(ObjViewer, {
        onClose: () => {
          this.checkReopenAdditionalFilesDialog();
        },
        url: material.downloadLink,
      });
    }

    if (material.type === VIDEO) {
      const videoUrl = material?.h264Path || material.path;
      this.dialogRepository.loadDialog(VideoWrapper, {
        onClose: () => {
          this.checkReopenAdditionalFilesDialog();
        },
        url: videoUrl || material.downloadLink,
      });
    }

    if (material.type === DOCS) {
      this.dialogRepository.loadDialog(PdfViewer, {
        onClose: () => {
          this.checkReopenAdditionalFilesDialog();
        },
        title: material?.title || material?.fileName || '',
        url: material?.downloadLink,
      });
    }
  }

  get userDetails$() {
    return this.userRepository.userDetails$;
  }

  get selectedCase$() {
    return this.casesRepository.selectedCase$;
  }

  get workspaceSignatureId$() {
    return this.authRepository.userWorkspace$.getValue()?.signatureId || null;
  }
}
